import { priceLine } from '../lib/priceLine';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Globe, Zap, Shield, Mail, ArrowRight, Palette, Star, Lock, Check, ChevronDown, Search, X, Package, RefreshCw, Sparkles, CreditCard } from 'lucide-react';
import api from '../lib/api';
import { normalizeDomainQuery } from '../lib/domainQuery';
import SiteFooter from './SiteFooter';

const INTENT_KEY = 'ep_intent';

function saveDomainIntent(payload) {
  try {
    sessionStorage.setItem(INTENT_KEY, JSON.stringify(payload));
  } catch (_) { /* ignore private mode */ }
  try {
    if (payload?.domain) sessionStorage.setItem('ep_preselected_domain', payload.domain);
  } catch (_) { /* ignore */ }
}

// Feature list is built at render time so the Professional Email price is sourced
// from the live /api/pricing (email.price) — never a hardcoded number that can drift
// from what the portal actually charges at checkout.
const buildFeatures = (emailPrice) => [
  { icon: Globe, title: 'Your Own Domain', desc: 'Search and claim your perfect web address. Over 480 extensions available with free privacy protection.' },
  { icon: Palette, title: 'Website Builder', desc: 'Describe your church or business and we build a complete, professional website for you.' },
  { icon: Sparkles, title: 'Ready in Minutes', desc: 'Describe what you do and we design and write a complete website for you — you can edit every word and photo yourself.' },
  { icon: Palette, title: 'Yours to Change', desc: 'Edit any wording, photo or colour yourself, whenever you like. No plugins, no updates, nothing to break.' },
  { icon: Shield, title: 'Always Secure', desc: 'Every page shows the secure lock icon. There are no plugins to update and nothing for you to patch.' },
  { icon: Lock, title: 'Nothing to Break', desc: 'Your website is pre-configured and simplified. Everything you need, nothing confusing.' },
  { icon: CreditCard, title: 'A basic shop', desc: 'Tickets, raffle, photos, donations. Visitors tap Buy and pay you on Venmo, Cash App, Zelle, or PayPal. When the money arrives, you send them what they bought.' },
];

// Live *.easypeazy.site sites. Milstead first (real church, active subscription).
// example: true = demo/QA-built site, shown with an "Example" tag (owner accounts
// are test accounts in ep_users, no payments), per CoS 2026-10-16.
const GALLERY = [
  { host: 'milstead.easypeazy.site', img: '/gallery/ep-milstead.jpg', name: 'Milstead Baptist Church', tag: 'A church in Milstead, Georgia' },
  { host: 'santas4u.easypeazy.site', img: '/gallery/ep-santas.jpg', name: 'Santas 4 U', tag: 'Bringing joy to every occasion' },
  { host: 'baxleysigns.easypeazy.site', img: '/gallery/ep-baxley.jpg', name: 'Signs of the Times, Inc.', tag: 'Family owned since 1915' },
  { host: 'marigold-flowers.easypeazy.site', img: '/gallery/ep-marigold.jpg', name: 'Marigold Flowers', tag: 'Georgia-grown, arranged by hand', example: true },
  { host: 'cedar-lane-bakery.easypeazy.site', img: '/gallery/ep-bakery.jpg', name: 'Cedar Lane Bakery', tag: 'Slow bread, sweet mornings', example: true },
  { host: 'willow-street-church.easypeazy.site', img: '/gallery/ep-willow.jpg', name: 'Willow Street Church', tag: 'A small church with a wide-open door', example: true },
];

const FEATURES = [
  { icon: Palette, title: 'We build it, you can change it', desc: 'Describe what you do and we design and write a complete website. Edit any word, photo, or color yourself, whenever you like.' },
  { icon: Lock, title: 'Your address, always secure', desc: 'Start on yourname.easypeazy.site or add your own domain. Shows the secure lock icon, with privacy protection and nothing to patch.' },
  { icon: CreditCard, title: 'A basic shop, included', desc: 'Tickets, raffles, photos, donations. Visitors tap Buy and pay you on Venmo, Cash App, Zelle, or PayPal.' },
  // True of every site the builder publishes (2026-10-22): business details for
  // Google Maps, a sitemap, one official address per page, a description per
  // page. No promise of rankings.
  { icon: Search, title: 'Built to be found on Google', desc: 'Every site carries your business details for Google Maps, a sitemap Google can follow, and search-friendly titles on every page. Included, not an add-on.' },
];

const categoryLabels = {
  popular: 'Popular',
  tech: 'Tech & Startup',
  business: 'Business',
  ecommerce: 'E-Commerce',
  creative: 'Creative',
  community: 'Community & Faith',
  food: 'Food & Hospitality',
  country: 'Country / Region',
  affordable: 'Affordable',
  professional: 'Professional',
};

function EmailCard() {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-6" data-testid="pricing-email">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-cyan-400" />
          <h4 className="font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Email forwarding</h4>
        </div>
        <span className="text-sm font-semibold text-cyan-300" style={{ fontFamily: 'Outfit' }}>Included</span>
      </div>
      <p className="text-sm text-slate-400">
        Mail to <span className="text-slate-200">yourname@easypeazy.site</span> arrives in the Gmail or Outlook you already use.
      </p>
      <p className="text-xs text-slate-500 mt-3">
        This is forwarding, not a separate inbox — you read it where you already read mail. Sending from that address, or a mailbox on your own domain, is set up by hand if you need it.
      </p>
    </div>
  );
}

function DomainCard({ domainData }) {
  const [open, setOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('popular');
  const [searchFilter, setSearchFilter] = useState('');

  const categories = domainData?.categories || {};
  const allTlds = Object.values(categories).flat();
  const filtered = searchFilter
    ? allTlds.filter(t => t.tld.includes(searchFilter.toLowerCase()))
    : (categories[activeCategory] || []);

  return (
    <div className={`rounded-xl border transition-all duration-300 overflow-hidden cursor-pointer ${open ? 'border-purple-500/40 bg-purple-500/5' : 'border-slate-800 bg-slate-950/50 hover:border-slate-700'}`} data-testid="pricing-domain" onClick={() => setOpen(!open)}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-purple-400" />
            <h4 className="font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Use your own web address</h4>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <span className="text-2xl font-bold text-white whitespace-nowrap" style={{ fontFamily: 'Outfit' }}>+ domain</span>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
          </div>
        </div>
        <p className="text-sm text-slate-400">{open ? `See pricing for ${domainData?.total_tlds_available || '480+' } web addresses:` : 'Website $12/year + your domain\'s yearly price, shown before you pay'}</p>
      </div>

      {open && (
        <div className="px-6 pb-6 animate-fade-in" onClick={e => e.stopPropagation()}>
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
            <input value={searchFilter} onChange={e => setSearchFilter(e.target.value)} className="w-full bg-slate-800/50 border border-slate-700 focus:border-purple-500 rounded-lg h-9 pl-9 pr-8 text-sm text-slate-100 outline-none placeholder:text-slate-600" placeholder="Search extensions..." data-testid="tld-search-input" />
            {searchFilter && <button onClick={() => setSearchFilter('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>}
          </div>

          {/* Category tabs */}
          {!searchFilter && (
            <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1 scrollbar-none">
              {Object.keys(categories).map(cat => (
                <button key={cat} onClick={() => setActiveCategory(cat)} className={`text-xs px-2.5 py-1 rounded-md whitespace-nowrap transition-all ${activeCategory === cat ? 'bg-purple-500/20 text-purple-400 font-medium' : 'text-slate-500 hover:text-slate-300'}`} data-testid={`cat-${cat}`}>
                  {categoryLabels[cat] || cat}
                </button>
              ))}
            </div>
          )}

          {/* TLD price grid — shows bundled website price */}
          <div className="grid grid-cols-2 gap-1.5 max-h-[220px] overflow-y-auto pr-1">
            {filtered.map((t, i) => (
              <div key={i} className="flex items-center justify-between bg-slate-800/30 rounded-lg px-3 py-2 border border-slate-800/50 hover:border-purple-500/20 transition-all" data-testid={`tld-${t.tld.replace('.', '')}`}>
                <span className="text-sm font-medium text-purple-300">{t.tld}</span>
                <div className="text-right">
                  <span className="text-xs font-semibold text-white">${t.website_renew}</span>
                  <span className="text-[10px] text-slate-600">/yr</span>
                  <p className="text-[9px] text-slate-500">Website $12 + domain ${t.renew}</p>
                </div>
              </div>
            ))}
            {filtered.length === 0 && <p className="text-xs text-slate-600 col-span-2 text-center py-4">No extensions found</p>}
          </div>

          <p className="text-[10px] text-slate-600 mt-2 text-center">Domain price per year, added to the $12 website. Showing {filtered.length} of {domainData?.total_tlds_available || '483'} extensions.</p>
          <Link to="/signup" className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 py-2 rounded-lg text-sm font-medium transition-all" data-testid="domain-cta-btn">
            Find Your Website <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
}

function HeroSearch({ cheapest }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // Whether the registrar actually confirmed availability. When false we must NOT present
  // any result as "available" — we only know the price, not whether the name is free.
  const [availChecked, setAvailChecked] = useState(true);
  const [searchError, setSearchError] = useState('');
  const [searchedTerm, setSearchedTerm] = useState('');

  const doSearch = async () => {
    const term = normalizeDomainQuery(query);
    if (!term || term.length < 2) return;
    setSearchedTerm(term);
    setSearching(true);
    setSearched(true);
    setShowAll(false);
    setSearchError('');
    try {
      const res = await api.searchDomainsPublic(term);
      setResults(res.data.results || []);
      const live = res.data.availability_checked === true || res.data.live === true;
      setAvailChecked(live && res.data.availability !== 'estimated');
    } catch (err) {
      setResults([]);
      setAvailChecked(false);
      setSearchError(err.response?.data?.detail || 'Could not search domains. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleGetStarted = (r) => {
    const intent = {
      domain: r.domain,
      price: r.website_price,
      website_price: r.website_price,
      website_renew: r.website_renew,
      available: r.available,
      is_premium: !!r.is_premium,
      availability: r.availability || (availChecked ? 'live' : 'estimated'),
    };
    saveDomainIntent(intent);
    const params = new URLSearchParams({
      domain: r.domain,
      price: String(r.website_price ?? ''),
      available: r.available == null ? 'unknown' : String(!!r.available),
    });
    navigate(`/signup?${params.toString()}`);
  };

  const visibleResults = showAll ? results : results.slice(0, 6);
  const hasMore = results.length > 6 && !showAll;

  return (
    <div className="mt-10 max-w-xl" data-testid="hero-search">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[12rem]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doSearch()}
            className="w-full bg-slate-900/80 border border-slate-700 focus:border-indigo-500 rounded-xl h-14 pl-12 pr-4 text-lg text-slate-100 outline-none placeholder:text-slate-600 backdrop-blur-sm"
            placeholder="Enter your business name..."
            data-testid="hero-search-input"
          />
        </div>
        <button
          onClick={doSearch}
          disabled={searching || !query.trim()}
          className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 rounded-xl text-base font-medium transition-all active:scale-95 shadow-lg shadow-indigo-500/20 disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
          data-testid="hero-search-btn"
        >
          {searching ? <RefreshCw className="w-5 h-5 animate-spin" /> : <><Search className="w-4 h-4" /> See Prices</>}
        </button>
      </div>

      {/* Results */}
      {searched && (
        <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/90 backdrop-blur-sm overflow-hidden animate-fade-in" data-testid="hero-search-results">
          {searchedTerm && !searching && (
            <p className="px-4 pt-3 text-xs text-slate-400 text-left" data-testid="hero-searched-term">Showing results for <strong className="text-slate-200">{searchedTerm}</strong></p>
          )}
          {searchError ? (
            <div className="p-4 text-center text-sm text-red-400" data-testid="hero-search-error">{searchError}</div>
          ) : results.length > 0 ? (
            <>
              {/* Value explanation header */}
              <div className="px-4 py-3 bg-emerald-500/5 border-b border-emerald-500/10">
                <div className="flex items-start gap-2.5">
                  <Package className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-emerald-300">Website $12 + domain = your yearly total</p>
                    <p className="text-[11px] text-slate-500 mt-1">Elsewhere you'd pay separately for each piece:</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-[10px]">
                      <span className="text-slate-500">Domain <span className="text-red-400/70 line-through">~$12/yr</span></span>
                      <span className="text-slate-500">Hosting <span className="text-red-400/70 line-through">~$96/yr</span></span>
                      <span className="text-slate-500">Design tool <span className="text-red-400/70 line-through">~$144/yr</span></span>
                      <span className="text-slate-500">SSL <span className="text-red-400/70 line-through">~$70/yr</span></span>
                      <span className="text-white font-medium">= $322+/yr elsewhere</span>
                    </div>
                    <p className="text-[11px] text-emerald-400 font-medium mt-1">Here? Starting at {`$${cheapest}`}/yr. Design, hosting, and a secure connection included. Email forwarding is extra setup, into the inbox you already use.</p>
                  </div>
                </div>
              </div>
              {!availChecked && (
                <div className="px-4 py-2.5 bg-amber-500/10 border-b border-amber-500/20 flex items-start gap-2" data-testid="hero-estimated-banner">
                  <Shield className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-300/90 leading-relaxed">
                    Estimated availability — we couldn't confirm live right now. Prices below are real; we'll verify the name is free before you pay.
                  </p>
                </div>
              )}
              <div className="divide-y divide-slate-800/50 max-h-[400px] overflow-y-auto">
                {visibleResults.map((r, i) => {
                  const isAvail = r.available === true;
                  const isTaken = availChecked && r.available === false;
                  return (
                    <div key={i} className={`px-4 py-3 ${isTaken ? 'opacity-40' : 'hover:bg-slate-800/20'}`} data-testid={`hero-result-${i}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${!availChecked ? 'bg-slate-500' : isAvail ? 'bg-emerald-400' : 'bg-red-400'}`} />
                          <div>
                            <span className="text-sm font-medium text-slate-200">{r.domain}</span>
                            {r.is_premium && <span className="ml-2 text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">Premium</span>}
                          </div>
                        </div>
                        {isTaken ? (
                          <span className="text-xs text-slate-600">Taken</span>
                        ) : (
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <span className={`text-sm font-semibold ${availChecked ? 'text-emerald-400' : 'text-slate-200'}`} data-testid={`hero-price-${i}`}>{priceLine(r)}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleGetStarted(r)}
                              className="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs transition-all"
                              data-testid={`hero-get-${i}`}
                            >
                              {availChecked ? 'Get Started' : 'Start'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Show more button */}
              {hasMore && (
                <button onClick={() => setShowAll(true)} className="w-full px-4 py-2.5 text-center text-sm text-indigo-400 hover:text-indigo-300 hover:bg-slate-800/30 transition-all border-t border-slate-800/50" data-testid="show-more-results">
                  Show {results.length - 6} more extensions <ChevronDown className="w-3.5 h-3.5 inline ml-1" />
                </button>
              )}
              {/* Comparison footer */}
              <div className="px-4 py-3 bg-slate-800/30 border-t border-slate-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-[10px] text-slate-500">
                    <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-400" /> Professional design</span>
                    <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-400" /> Written for you</span>
                    <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-400" /> SSL security</span>
                    <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-400" /> Hosting</span>
                  </div>
                  <span className="text-[10px] text-slate-600">All included in price</span>
                </div>
              </div>
            </>
          ) : (
            <div className="p-4 text-center text-sm text-slate-500">
              {searching ? 'Checking availability...' : 'No results found. Try a different name.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function LandingPage() {
  const [pricing, setPricing] = useState(null);
  const [platformPricing, setPlatformPricing] = useState(null);
  useEffect(() => {
    api.getDomainPricing().then(r => setPricing(r.data)).catch(() => {});
    api.getPricing().then(r => setPlatformPricing(r.data)).catch(() => {});
  }, []);
  // Headline price is the subdomain SKU checkout actually charges — not a leftover
  // TLD bundle. Falls back to the last-known live values if /api/pricing is down.
  const cheapest = platformPricing?.website?.subdomain_annual ?? platformPricing?.website?.starts_from ?? 12;
  const subdomainHalf = platformPricing?.website?.subdomain_half_year ?? 6;
  // Real Professional Email price = what /client/emails/checkout actually charges (email.price
  // from /api/pricing). Falls back to the last-known live value ($8) if the API is unreachable.
  // Note: email.extra_mailbox_price ($3) is only for additional mailboxes, never the headline price.
  const emailPrice = platformPricing?.email?.price ?? 8;

  return (
    <div className="min-h-screen bg-[#020617] overflow-x-hidden" style={{ fontFamily: 'Inter, system-ui, sans-serif' }} data-testid="landing-page">
      {/* Nav: single line at 390 */}
      <nav className="fixed top-0 w-full z-50 border-b border-white/5 bg-[#020617]/85 backdrop-blur-md" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-2 sm:gap-4">
          <Link to="/" className="flex items-center gap-2 min-h-[44px] mr-auto">
            <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>EasyPeazy</span>
          </Link>
          <Link to="/login" className="whitespace-nowrap inline-flex items-center min-h-[44px] text-slate-300 hover:text-white transition-colors px-3 sm:px-4 font-medium" data-testid="nav-login-btn">Sign in</Link>
          <Link to="/signup" className="whitespace-nowrap inline-flex items-center min-h-[44px] bg-indigo-500 hover:bg-indigo-600 text-white px-4 sm:px-5 rounded-lg font-semibold transition-all active:scale-95 shadow-lg shadow-indigo-500/20" data-testid="nav-signup-btn">Get started</Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-20 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/15 via-purple-500/5 to-transparent" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 rounded-full border border-indigo-400/40 bg-indigo-500/15 text-indigo-200 text-base font-semibold mb-7" data-testid="hero-price-pill">
              Preview free · ${cheapest} a year to publish
            </div>
            <h1 className="text-[44px] leading-[1.05] md:text-7xl font-bold text-white tracking-tight mb-6" style={{ fontFamily: 'Outfit' }}>
              Your website, <span className="text-indigo-400">made simple.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl">
              Tell us about your church or business and we'll design and write a free draft of your whole website. Like it? Pay ${cheapest} to publish it on your web address — hosting, the secure lock icon, and a basic shop included.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/signup" className="inline-flex items-center justify-center gap-2 min-h-[52px] bg-indigo-500 hover:bg-indigo-600 text-white px-8 rounded-lg text-lg font-semibold transition-all active:scale-95 shadow-lg shadow-indigo-500/25" data-testid="hero-cta-btn">
                Get started <ArrowRight className="w-5 h-5" />
              </Link>
              <a href="#pricing" className="inline-flex items-center justify-center min-h-[52px] bg-slate-800 hover:bg-slate-700 text-slate-100 px-8 rounded-lg text-lg border border-slate-700 transition-all">
                See what's included
              </a>
            </div>
          </div>

          {/* Gallery of live *.easypeazy.site sites */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14" data-testid="site-gallery">
            {GALLERY.map((g, i) => (
              <figure key={g.host} className={i >= 3 ? 'hidden md:block' : ''}>
                <a href={`https://${g.host}`} target="_blank" rel="noopener noreferrer" className="block rounded-xl overflow-hidden border border-slate-800 bg-[#0b1220] shadow-2xl shadow-black/40 hover:border-indigo-500/50 transition-colors">
                  <div className="h-8 bg-slate-800/80 flex items-center gap-1.5 px-3">
                    <i className="w-2.5 h-2.5 rounded-full bg-slate-600" /><i className="w-2.5 h-2.5 rounded-full bg-slate-600" /><i className="w-2.5 h-2.5 rounded-full bg-slate-600" />
                    <span className="ml-2 text-xs text-slate-400 truncate">{g.host}</span>
                  </div>
                  <div className="relative aspect-[16/10] bg-slate-900">
                    {g.example && <span className="absolute top-2.5 left-2.5 z-10 bg-white/95 text-slate-900 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-md shadow" data-testid="gallery-example-tag">Example</span>}
                    <img src={g.img} alt={`${g.name} website`} width={1280} height={800} loading={i < 3 ? 'eager' : 'lazy'} className="w-full h-full object-cover object-top" />
                  </div>
                </a>
                <figcaption className="mt-3 text-white font-semibold">{g.name}<span className="block font-normal text-slate-400">{g.tag}</span></figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Features: three cards */}
      <section className="py-20 md:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-3xl md:text-5xl font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit' }}>Everything you need, nothing to break.</h2>
            <p className="text-slate-400 text-lg mt-3">No plugins, no updates, no tech skills.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f, i) => (
              <div key={i} className="rounded-2xl border border-slate-800 bg-[#0b1220] p-8" data-testid={`feature-card-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center mb-5">
                  <f.icon className="w-6 h-6 text-indigo-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: 'Outfit' }}>{f.title}</h3>
                <p className="text-slate-300 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 md:py-24 px-4 sm:px-6 bg-gradient-to-b from-transparent to-[#0a1022]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-10">
            <div>
              <h2 className="text-3xl md:text-5xl font-semibold text-white tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>One price. Your whole website.</h2>
              <p className="text-slate-300 text-lg mb-4">See your website before you pay anything. When you're happy, ${cheapest} a year publishes it — design, writing, security, and updates included. Same price every year.</p>
              <p className="text-slate-400 mb-2">Want your own domain (like .com)? Add its yearly price, shown before you pay.</p>
              <p className="text-slate-400">Email forwarding is included: mail to your address arrives in the inbox you already use.</p>
            </div>
            <div className="rounded-2xl border border-indigo-500/50 bg-indigo-500/5 p-8 shadow-[0_0_40px_-8px_rgba(99,102,241,0.3)]" data-testid="pricing-hosting">
              <p className="text-indigo-200 font-semibold">yourname.easypeazy.site</p>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="text-6xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>${cheapest}</span>
                <span className="text-slate-400 text-lg">/ year to publish</span>
              </div>
              <p className="text-slate-400 mt-1">Free to preview · or two payments of ${subdomainHalf}</p>
              <div className="grid grid-cols-2 gap-2.5 my-6">
                {['Your own web address', 'Professional design', 'Written for you', 'Security certificate', 'Automatic updates', 'Edit it yourself', 'Basic shop', 'Privacy protection'].map((f) => (
                  <div key={f} className="flex items-center gap-2 text-slate-200">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" /> {f}
                  </div>
                ))}
              </div>
              <Link to="/signup" className="w-full inline-flex items-center justify-center gap-2 min-h-[52px] bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-semibold transition-all active:scale-95" data-testid="pricing-cta-btn">
                Get started <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="max-w-4xl mx-auto">
            {/* Add-ons — Interactive */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <EmailCard />
              <DomainCard domainData={pricing} />
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
